import { createContext, useContext, useEffect, useState } from 'react';
import { Session, AuthError } from '@supabase/supabase-js';
import { supabase, SupabaseUser } from '../lib/supabase';

interface AuthContextType {
  session: Session | null;
  user: SupabaseUser | null;
  signOut: () => Promise<{ error: AuthError | null }>;
  isLoading: boolean;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Function to refresh the session state
  const refreshSession = async () => {
    try {
      setIsLoading(true);
      // First try to get the current session
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (sessionData.session) {
        setSession(sessionData.session);
        setUser(sessionData.session.user);
      } else {
        // If no session, attempt to refresh it
        const { data: refreshData } = await supabase.auth.refreshSession();
        if (refreshData.session) {
          setSession(refreshData.session);
          setUser(refreshData.session.user);
        } else {
          // Clear user if no valid session can be found
          setSession(null);
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
      // Clear state on error
      setSession(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Development-only: use fixed dev user when running on localhost with env tokens set.
    // Set VITE_DEV_AUTH_ACCESS_TOKEN and VITE_DEV_AUTH_REFRESH_TOKEN in .env.development
    // or .env.local (e.g. from one real login, then copy from Application > Local Storage).
    const isLocalDev =
      import.meta.env.DEV &&
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    const devAccessToken = import.meta.env.VITE_DEV_AUTH_ACCESS_TOKEN;
    const devRefreshToken = import.meta.env.VITE_DEV_AUTH_REFRESH_TOKEN;

    // Get initial session
    const initializeAuth = async () => {
      setIsLoading(true);
      try {
        // Check if auth callback is in progress - if so, minimize processing
        const authInProgress = sessionStorage.getItem('authCallbackInProgress') === 'true';
        if (authInProgress) {
          // Do minimal processing - just get the session to update UI state
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            setSession(session);
            setUser(session.user ?? null);
          }
          setIsLoading(false);
          return;
        }

        const { data: { session } } = await supabase.auth.getSession();
        
        // Development-only: use fixed dev user only if no existing valid session
        // This prevents overwriting a refreshed session from a previous day with expired dev tokens
        if (!session && isLocalDev && devAccessToken && devRefreshToken) {
          console.log('[Dev auth] No existing session, using dev tokens from .env.development');
          const { error: setError } = await supabase.auth.setSession({
            access_token: devAccessToken,
            refresh_token: devRefreshToken,
          });
          if (setError) {
            console.warn('[Dev auth] setSession failed:', setError.message);
          }
          // Re-fetch session after setting dev tokens
          const { data: { session: devSession } } = await supabase.auth.getSession();
          if (devSession) {
            setSession(devSession);
            setUser(devSession.user ?? null);
            setIsLoading(false);
            return;
          }
        }
        
        if (session) {
          setSession(session);
          setUser(session.user ?? null);
        } else {
          // Check URL for potential tokens (hash fragment)
          if (window.location.hash && window.location.hash.includes('access_token')) {
            // Force Supabase to process the hash fragment
            const { data, error } = await supabase.auth.getUser();
            
            if (data.user) {
              // Refresh the session data after token processing
              const { data: refreshData } = await supabase.auth.getSession();
              if (refreshData.session) {
                setSession(refreshData.session);
                setUser(refreshData.session.user);
              }
            } else if (error) {
              console.error('Error processing URL tokens:', error);
            }
          } else {
            // Try to refresh session as a fallback
            const { data: refreshData } = await supabase.auth.refreshSession();
            if (refreshData.session) {
              setSession(refreshData.session);
              setUser(refreshData.session.user);
            }
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    // Development: Auto-refresh session every 5 minutes to prevent expiration
    // This ensures the dev user stays logged in by refreshing before tokens expire
    let devRefreshInterval: NodeJS.Timeout | null = null;
    if (isLocalDev && devAccessToken && devRefreshToken) {
      devRefreshInterval = setInterval(async () => {
        try {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          if (currentSession) {
            // Refresh the session to get new tokens
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            if (refreshError) {
              console.warn('[Dev auth] Auto-refresh failed:', refreshError.message);
            } else if (refreshData.session) {
              console.log('[Dev auth] Session auto-refreshed successfully');
            }
          }
        } catch (error) {
          console.warn('[Dev auth] Auto-refresh error:', error);
        }
      }, 5 * 60 * 1000); // Every 5 minutes
    }

    return () => {
      subscription.unsubscribe();
      if (devRefreshInterval) {
        clearInterval(devRefreshInterval);
      }
    };
  }, []);

  const value = {
    session,
    user,
    signOut: () => supabase.auth.signOut(),
    isLoading,
    refreshSession
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 
